import * as Sentry from '@sentry/react';

const isProduction = process.env.NODE_ENV === 'production';
const SENTRY_DSN =
    process.env.REACT_APP_SENTRY_DSN ||
    (isProduction
        ? 'https://73eaeb66502edf53a7828976b77c63fe@o4510108292939776.ingest.us.sentry.io/4510108293922816'
        : undefined);

if (isProduction && SENTRY_DSN) {
    Sentry.init({
        dsn: SENTRY_DSN,
        environment: 'production',
        sendDefaultPii: true,
        enableLogs: true,
        integrations: [
            Sentry.browserTracingIntegration(),
        ],
        tracePropagationTargets: [
            'localhost',
            /^https:\/\/app-podscope-backend\.onrender\.com/,
            /^https:\/\/podscope-app-backend\.onrender\.com/,
        ],
        tracesSampleRate: 0.1,
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
        release: process.env.REACT_APP_VERSION || 'unknown',
    });
}

// Sentry service interface similar to analytics
export const sentry = {
    // Capture an exception
    captureException: (error: Error, context?: Record<string, any>) => {
        if (context) {
            Sentry.withScope((scope: Sentry.Scope) => {
                Object.entries(context).forEach(([key, value]) => {
                    scope.setContext(key, value);
                });
                Sentry.captureException(error);
            });
        } else {
            Sentry.captureException(error);
        }
    },

    // Capture a message
    captureMessage: (message: string, level: Sentry.SeverityLevel = 'info', context?: Record<string, any>) => {
        if (context) {
            Sentry.withScope((scope: Sentry.Scope) => {
                Object.entries(context).forEach(([key, value]) => {
                    scope.setContext(key, value);
                });
                Sentry.captureMessage(message, level);
            });
        } else {
            Sentry.captureMessage(message, level);
        }
    },

    // Set user context
    setUser: (user: {
        id?: string;
        email?: string;
        username?: string;
        name?: string;
        company?: string;
        role?: string;
        [key: string]: any;
    }) => {
        Sentry.setUser(user);
    },

    // Set additional context
    setContext: (key: string, context: Record<string, any>) => {
        Sentry.setContext(key, context);
    },

    // Set tags
    setTag: (key: string, value: string) => {
        Sentry.setTag(key, value);
    },

    // Set tags in bulk
    setTags: (tags: Record<string, string>) => {
        Sentry.setTags(tags);
    },

    // Add breadcrumb
    addBreadcrumb: (breadcrumb: {
        message?: string;
        category?: string;
        level?: Sentry.SeverityLevel;
        data?: Record<string, any>;
    }) => {
        Sentry.addBreadcrumb(breadcrumb);
    },

    // Start a transaction for performance monitoring
    startTransaction: (name: string, op?: string) => {
        return Sentry.startSpan({ name, op }, (span: Sentry.Span) => span);
    },

    // Clear user and context
    clearUser: () => {
        Sentry.setUser(null);
    },

    // Configure scope
    configureScope: (callback: (scope: Sentry.Scope) => void) => {
        Sentry.withScope(callback);
    },

    // Show report dialog
    showReportDialog: (options?: Sentry.ReportDialogOptions) => {
        Sentry.showReportDialog(options);
    },
};

// Error boundary component
export const SentryErrorBoundary = Sentry.ErrorBoundary;

// Profiler component for performance monitoring
export const SentryProfiler = Sentry.Profiler;

// Higher-order component for error boundaries
export const withSentryErrorBoundary = Sentry.withErrorBoundary;

// Hook for performance monitoring
export const useSentryTransaction = (name: string, op?: string) => {
    return {
        startSpan: <T>(callback: (span: Sentry.Span) => T) => Sentry.startSpan({ name, op }, callback),
        withSpan: <T>(callback: () => T) => Sentry.startSpan({ name, op }, callback),
    };
};

export default sentry;
